// src/component/RibbonBubbleAdmin.js
import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Button,
  TextField,
  Checkbox,
  FormGroup,
} from "@mui/material";
import MilitaryTechRoundedIcon from "@mui/icons-material/MilitaryTechRounded";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";

const DOC_PATH = "systemConfig";
const DOC_ID = "ribbonBubble";

const DEFAULTS = {
  enabled: false,
  text: "支払いはこちら",
  hideForPhases: [], // この期を支払い済みのユーザーには非表示
};

const MAX_PHASE = 6; // 1期〜6期まで選べる

export default function RibbonBubbleAdmin() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const surface = theme.surfaceBg ?? "#FFFFFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(DEFAULTS.enabled);
  const [text, setText] = useState(DEFAULTS.text);
  const [hideForPhases, setHideForPhases] = useState(DEFAULTS.hideForPhases);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const showSnack = useCallback((message, type = "success") => {
    setSnackbar({ open: true, message, type });
  }, []);

  useEffect(() => {
    const ref = doc(db, DOC_PATH, DOC_ID);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const merged = { ...DEFAULTS, ...data };
        setEnabled(merged.enabled === true);
        setText(merged.text || DEFAULTS.text);
        setHideForPhases(
          Array.isArray(merged.hideForPhases)
            ? merged.hideForPhases.map(Number).filter(Number.isFinite).sort((a, b) => a - b)
            : []
        );
        setLoading(false);
      },
      (err) => {
        console.error("RibbonBubbleAdmin onSnapshot error:", err);
        setLoading(false);
        showSnack("設定の取得に失敗しました。", "error");
      }
    );
    return () => unsub();
  }, [showSnack]);

  const handleSave = async () => {
    try {
      const ref = doc(db, DOC_PATH, DOC_ID);
      await setDoc(
        ref,
        {
          enabled: Boolean(enabled),
          text: text || DEFAULTS.text,
          hideForPhases: hideForPhases.map(Number).filter(Number.isFinite).sort((a, b) => a - b),
          updatedAt: new Date(),
        },
        { merge: true }
      );
      showSnack("吹き出し設定を保存しました。");
    } catch (err) {
      console.error("RibbonBubbleAdmin save error:", err);
      showSnack("保存に失敗しました。", "error");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.18em",
            fontSize: 11,
            opacity: 0.8,
            color: textBase,
          }}
        >
          ADMIN
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase }}
        >
          リボン吹き出し設定
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}>
          リボンボタンの横に表示される吹き出しのON/OFFとテキストを設定します。
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              background:
                "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                `linear-gradient(135deg, ${accent}22, ${accent}11)`,
              boxShadow:
                "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <MilitaryTechRoundedIcon sx={{ fontSize: 30, color: accent }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: textBase, mb: 0.5 }}
            >
              吹き出し表示
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
              リボンボタン付近に吹き出しメッセージを表示します。
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2.5}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontWeight: 700, color: textBase }}>
                {enabled ? "吹き出し：表示中" : "吹き出し：非表示"}
              </Typography>
            }
          />

          <TextField
            label="吹き出しテキスト"
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            helperText="リボンボタンの横に表示されるメッセージです。"
          />

          <Divider />

          <Box>
            <Typography sx={{ fontWeight: 800, color: textBase, mb: 1 }}>
              以下の期を支払い済みのユーザーには非表示
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mb: 1.5 }}>
              チェックした期の支払いがあるユーザーには吹き出しを表示しません。
            </Typography>
            <FormGroup row>
              {Array.from({ length: MAX_PHASE }, (_, i) => i + 1).map((phase) => (
                <FormControlLabel
                  key={phase}
                  control={
                    <Checkbox
                      checked={hideForPhases.includes(phase)}
                      onChange={(e) => {
                        setHideForPhases((prev) =>
                          e.target.checked
                            ? [...prev, phase].sort((a, b) => a - b)
                            : prev.filter((p) => p !== phase)
                        );
                      }}
                    />
                  }
                  label={`${phase}期`}
                />
              ))}
            </FormGroup>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 0.5 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                fontWeight: 800,
                px: 3,
                bgcolor: accent,
                boxShadow: "0 10px 24px rgba(20,16,40,0.22)",
                "&:hover": {
                  bgcolor: accent,
                  boxShadow: "0 14px 28px rgba(20,16,40,0.28)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              保存
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
